import { useMemo } from 'react'
import getAPIDomain from '@signal/utils/env/apiDomain'
import {
  UserAccountType,
  UserDetailsType,
  UserAccountFetchHookParamsType,
  DealerDetailsFetchHookParamsType,
  DealerDetailsType
} from './types'
import useSWRWithCache from '../swr/hooks'
import { FetchHandlerType } from '../swr/types'
import { fetchData, fetchDataNull } from '../swr/fetch'
import mockDealerDetails from './__tests__/mockDealerDetails.json'

export function getUserAccountsPath(): string {
  return `${getAPIDomain('/menu/service')}/v1/accounts`
}

export function getUserDetailsPath(): string {
  return `${getAPIDomain('/sheriff/authorize')}/v1/me/details`
}

export function getDealerDetailsPath(accountId: string): string {
  return `${getAPIDomain('/accounts/info')}/v1/account/info/${accountId}`
}

/**
 * this is a wrapper around useSWRWithCache that can be used to fetch the user accounts
 * @return {UserAccountsType | undefined} - returns undefined unless the data has been fetched,
 *   in which case the account data is returned
 */
export function useUserAccounts(): UserAccountType[] | undefined {
  const url = useMemo(() => getUserAccountsPath(), [])
  return useSWRWithCache({
    cacheBust: true,
    context: 'useUserAccounts',
    url
  })
}

export function useCachedUserAccounts(): UserAccountType[] | undefined {
  const url = useMemo(() => getUserAccountsPath(), [])
  return useSWRWithCache({
    cacheBust: false,
    context: 'useCachedUserAccounts',
    url
  })
}

/**
 * Generates the URL for a single user account.
 *
 * @param {string} accountId - The ID of the account to generate the URL for.
 * @returns {string} The generated URL.
 */
export function getUserSingleAccountsPath(accountId: string): string {
  return `${getAPIDomain('/menu/service')}/v1/accounts/${accountId}`
}

/**
 * Get the information for a single user account.
 * @param accountId The ID of the user account.
 * @returns The user account object or undefined.
 */
export function useSingleUserAccount({
  accountId
}: UserAccountFetchHookParamsType): UserAccountType[] | undefined {
  const url = useMemo(() => getUserSingleAccountsPath(accountId as string), [
    accountId
  ])
  return useSWRWithCache<UserAccountType[]>({
    cacheBust: true,
    context: 'useSingleUserAccount',
    url,
    transformFunc: (data: unknown): UserAccountType[] => {
      if (data) return [data] as UserAccountType[]
      return []
    }
  })
}

/**
 * this is a wrapper around useSWRWithCache that can be used to fetch the user details
 * @return {UserDetailsType | undefined} - returns undefined unless the data has been fetched,
 *   in which case the user data is returned
 */
export function useUserDetails(): UserDetailsType | undefined {
  return useSWRWithCache<UserDetailsType>({
    cacheBust: true,
    context: 'useUserDetails',
    url: getUserDetailsPath()
  })
}

/**
 * this is a wrapper around useSWRWithCache that can be used to fetch the dealer details
 * @return {UserDetailsType | undefined} - returns undefined unless the data has been fetched,
 *   in which case the dealer data is returned
 */
export function useDealerDetails(
  params: Pick<DealerDetailsFetchHookParamsType, 'accountId' | 'useMockData'>
): DealerDetailsType | undefined {
  const accountId = params?.accountId
  return useSWRWithCache<DealerDetailsType>({
    cacheBust: true,
    context: 'useDealerDetails',
    fetchHandler: (accountId ? fetchData : fetchDataNull) as FetchHandlerType<
      DealerDetailsType
    >,
    // TODO: remove useMockData when we can safely hit the endpoint here
    useMockData: true,
    mockData: mockDealerDetails,
    url: getDealerDetailsPath(accountId || '')
  })
}
